import React, { useRef, useState } from 'react';
import { Alert, Button, Form, Spinner } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { login } from '../api';
import './AdminLogin.css';

const AdminLogin: React.FC = () => {
  const [adminId, setAdminId] = useState('');
  const [errors, setErrors] = useState({
    adminId: '',
    password: '',
    submit: '',
  });
  const [touched, setTouched] = useState({
    adminId: false,
    password: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const passwordInputRef = useRef<HTMLInputElement | null>(null);
  const navigate = useNavigate();

  const validateField = (name: 'adminId' | 'password', value: string) => {
    const trimmedValue = value.trim();

    if (name === 'adminId') {
      if (!trimmedValue) return 'Admin ID is required.';
      if (trimmedValue.length < 3) return 'Admin ID must be at least 3 characters.';
      return '';
    }

    if (!trimmedValue) return 'Password is required.';
    if (trimmedValue.length < 6) return 'Password must be at least 6 characters.';
    return '';
  };

  const validateForm = () => {
    const passwordValue = passwordInputRef.current?.value ?? '';
    const nextErrors = {
      adminId: validateField('adminId', adminId),
      password: validateField('password', passwordValue),
      submit: '',
    };
    setErrors(nextErrors);
    return !nextErrors.adminId && !nextErrors.password;
  };

  const handleAdminIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setAdminId(value);
    setErrors((prev) => ({
      ...prev,
      adminId: touched.adminId ? validateField('adminId', value) : '',
      submit: '',
    }));
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setErrors((prev) => ({
      ...prev,
      password: touched.password ? validateField('password', value) : '',
      submit: '',
    }));
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name !== 'adminId' && name !== 'password') return;

    setTouched((prev) => ({ ...prev, [name]: true }));
    setErrors((prev) => ({ ...prev, [name]: validateField(name, value) }));
  };

  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setTouched({ adminId: true, password: true });
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const payload = {
        adminId: adminId.trim(),
        password: passwordInputRef.current?.value ?? '',
      };
      const res = await login(payload);
      localStorage.setItem('token', res.data.token);
      setAdminId('');
      setErrors({ adminId: '', password: '', submit: '' });
      navigate('/dashboard');
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        'Login failed. Please check your credentials and try again.';
      setErrors((prev) => ({ ...prev, submit: message }));
    } finally {
      if (passwordInputRef.current) {
        passwordInputRef.current.value = '';
      }
      setIsSubmitting(false);
    }
  };

  return (
    <main className="sf-admin-login">
      <div className="sf-admin-login-card">
        <div className="sf-admin-login-brand">
          <img src={`${process.env.PUBLIC_URL}/foundation-icon.svg`} alt="" width="56" height="56" />
          <div><p>Srisailam Foundation</p><span>Administration Portal</span></div>
        </div>
              <h1>Admin Login</h1>
              <p className="sf-admin-login-intro">Sign in to access the administration portal.</p>
              {errors.submit && (
                <Alert variant="danger" className="mb-3">
                  {errors.submit}
                </Alert>
              )}
              <Form noValidate onSubmit={handleLoginSubmit}>
                <Form.Group className="mb-3" controlId="formAdminId">
                  <Form.Label>Admin ID</Form.Label>
                  <Form.Control
                    type="text"
                    name="adminId"
                    value={adminId}
                    onChange={handleAdminIdChange}
                    onBlur={handleBlur}
                    placeholder="Enter Admin ID"
                    autoComplete="username"
                    required
                    aria-invalid={touched.adminId && Boolean(errors.adminId)}
                    aria-describedby={touched.adminId && errors.adminId ? 'admin-id-error' : undefined}
                    isInvalid={touched.adminId && Boolean(errors.adminId)}
                  />
                  <Form.Control.Feedback id="admin-id-error" type="invalid">{errors.adminId}</Form.Control.Feedback>
                </Form.Group>
                <Form.Group className="mb-3" controlId="formPassword">
                  <Form.Label>Password</Form.Label>
                  <div className="sf-admin-password">
                  <Form.Control
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    ref={passwordInputRef}
                    onChange={handlePasswordChange}
                    onBlur={handleBlur}
                    placeholder="Enter Password"
                    autoComplete="current-password"
                    required
                    aria-invalid={touched.password && Boolean(errors.password)}
                    aria-describedby={touched.password && errors.password ? 'admin-password-error' : undefined}
                    isInvalid={touched.password && Boolean(errors.password)}
                  />
                  <button type="button" className="sf-admin-password-toggle" aria-label={showPassword ? 'Hide password' : 'Show password'} aria-pressed={showPassword} onClick={() => setShowPassword((previous) => !previous)}>{showPassword ? 'Hide' : 'Show'}</button>
                  </div>
                  {touched.password && errors.password && <div id="admin-password-error" className="sf-admin-field-error">{errors.password}</div>}
                </Form.Group>
                <div className="text-center">
                  <Button variant="primary" type="submit" className="sf-admin-login-submit" disabled={isSubmitting}>
                    {isSubmitting ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                          className="me-2"
                        />
                        Logging in...
                      </>
                    ) : (
                      'Login'
                    )}
                  </Button>
                </div>
              </Form>
        <Link className="sf-admin-back-link" to="/">← Back to Website</Link>
      </div>
    </main>
  );
};

export default AdminLogin;
